import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminManagementService } from '../../services/adminService';
import { Search, Edit2 } from 'lucide-react';
import Pagination from '../../components/admin/Pagination';

function AdminSubscriptions() {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = parseInt(searchParams.get('page')) || 1;

    const [data, setData] = useState({ data: [], total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [planFilters, setPlanFilters] = useState([]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            if (searchTerm !== debouncedSearch) {
                setSearchParams(prev => {
                    const params = new URLSearchParams(prev);
                    params.set('page', '1');
                    return params;
                });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        loadSubs();
    }, [currentPage, debouncedSearch, planFilters]);
    const [editingSub, setEditingSub] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        loadSubs();
    }, [currentPage]);

    const loadSubs = async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                limit: 20,
                search: debouncedSearch
            };
            if (planFilters.length > 0) {
                params.plan = planFilters;
            }
            const response = await adminManagementService.getSubscriptions(params);
            setData(response);
        } catch (error) {
            console.error('Failed to load subscriptions', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setSearchParams({ page: newPage });
    };

    const handleEditClick = (sub) => {
        setEditingSub({
            id: sub.id,
            workspaceName: sub.workspace?.name,
            plan: sub.plan,
            status: sub.status,
            quotas: { ...sub.quotas },
            usageStats: { ...sub.usageStats },
            lastChargeAt: sub.lastChargeAt,
            nextChargeAt: sub.nextChargeAt,
            currentPeriodStart: sub.currentPeriodStart,
            currentPeriodEnd: sub.currentPeriodEnd,
            lastFailureReason: sub.lastFailureReason,
            retryCount: sub.retryCount,
            cancelAtPeriodEnd: sub.cancelAtPeriodEnd,
            activeCouponId: sub.activeCouponId
        });
        setError(null);
        setSuccess(null);
    };

    const handleInputChange = (section, key, value, type = 'number') => {
        setEditingSub(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [key]: type === 'boolean' ? value : (parseInt(value) || 0)
            }
        }));
    };

    const handleBaseChange = (e) => {
        const { name, value } = e.target;
        setEditingSub(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);
        try {
            await adminManagementService.updateSubscription(editingSub.id, {
                plan: editingSub.plan,
                status: editingSub.status,
                quotas: editingSub.quotas,
                usageStats: editingSub.usageStats
            });
            setSuccess('Subscription updated successfully');
            await loadSubs();
            setTimeout(() => setEditingSub(null), 1500);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update subscription');
        } finally {
            setIsSaving(false);
        }
    };

    const subs = data.data || [];

    const togglePlanFilter = (plan) => {
        setPlanFilters(prev => {
            const next = prev.includes(plan) ? prev.filter(p => p !== plan) : [...prev, plan];
            setSearchParams(params => {
                const newParams = new URLSearchParams(params);
                newParams.set('page', '1');
                return newParams;
            });
            return next;
        });
    };

    if (loading) return <div className="admin-loading">Loading subscriptions...</div>;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <h2>Subscription Management ({data.total})</h2>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div className="admin-search-bar">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search by workspace..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', background: '#1e293b', padding: '0.25rem', borderRadius: '4px', border: '1px solid #334155' }}>
                        {['free', 'basic', 'pro', 'business', 'enterprise'].map(plan => (
                            <button
                                key={plan}
                                onClick={() => togglePlanFilter(plan)}
                                style={{
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '3px',
                                    border: 'none',
                                    background: planFilters.includes(plan) ? '#3b82f6' : 'transparent',
                                    color: planFilters.includes(plan) ? 'white' : '#94a3b8',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    textTransform: 'capitalize'
                                }}
                            >
                                {plan}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Workspace</th>
                            <th>Plan</th>
                            <th>Status</th>
                            <th>Quotas (Mins)</th>
                            <th>Current Usage</th>
                            <th>Cycle</th>
                            <th>Provider</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subs.map(sub => (
                            <tr key={sub.id}>
                                <td>{sub.workspace?.name || 'Unknown'}</td>
                                <td>
                                    <span className={`plan-badge ${sub.plan}`}>{sub.plan.toUpperCase()}</span>
                                </td>
                                <td>
                                    <span className={`status-badge ${sub.status === 'active' ? 'success' : 'warning'}`}>
                                        {sub.status}
                                    </span>
                                </td>
                                <td>{sub.quotas?.executionMinutesPerMonth === -1 ? 'Unlimited' : sub.quotas?.executionMinutesPerMonth}</td>
                                <td>{sub.usageStats?.executionMinutesThisMonth || 0} mins</td>
                                <td>{sub.billingCycle}</td>
                                <td>{sub.provider}</td>
                                <td>
                                    <button className="icon-btn" onClick={() => handleEditClick(sub)}>
                                        <Edit2 size={18} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={data.totalPages}
                onPageChange={handlePageChange}
            />

            {/* Edit Subscription Modal */}
            {editingSub && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal">
                        <div className="admin-modal-header">
                            <h3>Edit Subscription: {editingSub.workspaceName}</h3>
                            <button className="close-btn" onClick={() => setEditingSub(null)}>×</button>
                        </div>
                        <form onSubmit={handleSave} className="admin-modal-body">
                            {error && <div className="admin-form-error">{error}</div>}
                            {success && <div className="admin-form-success">{success}</div>}

                            <div className="form-section">
                                <h4>Base Settings</h4>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Plan</label>
                                        <select name="plan" value={editingSub.plan} onChange={handleBaseChange}>
                                            <option value="free">Free</option>
                                            <option value="basic">Basic</option>
                                            <option value="pro">Pro</option>
                                            <option value="business">Business</option>
                                            <option value="enterprise">Enterprise</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select name="status" value={editingSub.status} onChange={handleBaseChange}>
                                            <option value="active">Active</option>
                                            <option value="canceled">Canceled</option>
                                            <option value="past_due">Past Due</option>
                                            <option value="paused">Paused</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h4>Quotas</h4>
                                <div className="quota-grid">
                                    <div className="form-group">
                                        <label>Exec Minutes (-1=Inf)</label>
                                        <input
                                            type="number"
                                            value={editingSub.quotas.executionMinutesPerMonth}
                                            onChange={(e) => handleInputChange('quotas', 'executionMinutesPerMonth', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Max Members</label>
                                        <input
                                            type="number"
                                            value={editingSub.quotas.maxTeamMembers}
                                            onChange={(e) => handleInputChange('quotas', 'maxTeamMembers', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="quota-grid">
                                    <div className="form-group">
                                        <label>Max Apps</label>
                                        <input
                                            type="number"
                                            value={editingSub.quotas.maxApps}
                                            onChange={(e) => handleInputChange('quotas', 'maxApps', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Max Concurrency</label>
                                        <input
                                            type="number"
                                            value={editingSub.quotas.maxConcurrency || 1}
                                            onChange={(e) => handleInputChange('quotas', 'maxConcurrency', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="quota-grid" style={{ marginTop: '0.5rem' }}>
                                    <div className="" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                                        <input
                                            type="checkbox"
                                            id="realDevicesEnabled"
                                            checked={editingSub.quotas.realDevicesEnabled || false}
                                            onChange={(e) => handleInputChange('quotas', 'realDevicesEnabled', e.target.checked, 'boolean')}
                                        />
                                        <label htmlFor="realDevicesEnabled" style={{ marginBottom: 0 }}>Enable Real Devices</label>
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h4>Usage Stats</h4>
                                <div className="quota-grid">
                                    <div className="form-group">
                                        <label>Minutes Used</label>
                                        <input
                                            type="number"
                                            value={editingSub.usageStats.executionMinutesThisMonth}
                                            onChange={(e) => handleInputChange('usageStats', 'executionMinutesThisMonth', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h4>Billing Details (Read-only)</h4>
                                <div className="metadata-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', background: '#111827', padding: '1rem', borderRadius: '8px', border: '1px solid #334155' }}>
                                    <div className="metadata-item">
                                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>Last Charge</label>
                                        <div style={{ fontSize: '0.9rem' }}>{editingSub.lastChargeAt ? new Date(editingSub.lastChargeAt).toLocaleString() : 'Never'}</div>
                                    </div>
                                    <div className="metadata-item">
                                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>Next Charge</label>
                                        <div style={{ fontSize: '0.9rem' }}>{editingSub.nextChargeAt ? new Date(editingSub.nextChargeAt).toLocaleString() : 'N/A'}</div>
                                    </div>
                                    <div className="metadata-item">
                                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>Period Start</label>
                                        <div style={{ fontSize: '0.9rem' }}>{editingSub.currentPeriodStart ? new Date(editingSub.currentPeriodStart).toLocaleString() : 'N/A'}</div>
                                    </div>
                                    <div className="metadata-item">
                                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>Period End</label>
                                        <div style={{ fontSize: '0.9rem' }}>{editingSub.currentPeriodEnd ? new Date(editingSub.currentPeriodEnd).toLocaleString() : 'N/A'}</div>
                                    </div>
                                    <div className="metadata-item">
                                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>Cancel at Term?</label>
                                        <div style={{ fontSize: '0.9rem', color: editingSub.cancelAtPeriodEnd ? '#ef4444' : '#10b981' }}>
                                            {editingSub.cancelAtPeriodEnd ? 'Yes' : 'No'}
                                        </div>
                                    </div>
                                    <div className="metadata-item">
                                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>Using Coupon?</label>
                                        <div style={{ fontSize: '0.9rem' }}>{editingSub.activeCouponId ? 'Yes' : 'No'}</div>
                                    </div>
                                    {editingSub.retryCount > 0 && (
                                        <div className="metadata-item">
                                            <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>Retry Count</label>
                                            <div style={{ fontSize: '0.9rem', color: '#f59e0b' }}>{editingSub.retryCount}</div>
                                        </div>
                                    )}
                                    {editingSub.lastFailureReason && (
                                        <div className="metadata-item" style={{ gridColumn: 'span 2' }}>
                                            <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>Last Failure Reason</label>
                                            <div style={{ fontSize: '0.9rem', color: '#ef4444', background: 'rgba(239, 68, 68, 0.1)', padding: '6px', borderRadius: '4px' }}>
                                                {editingSub.lastFailureReason}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="admin-modal-footer">
                                <button type="button" className="admin-cancel-btn" onClick={() => setEditingSub(null)}>
                                    Cancel
                                </button>
                                <button type="submit" className="admin-save-btn" disabled={isSaving}>
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminSubscriptions;
